/**
 * Host-side participant moderation panel — flags suspicious joins
 * (duplicate names, shared device/IP, joined mid-quiz) and lets the host
 * remove participants (device gets blocklisted for the session).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../ui/alert-dialog';
import { UserX, Users, Copy, MonitorSmartphone, Clock3, Loader2, ShieldAlert } from 'lucide-react';

export function ParticipantModerationPanel({ session, onUpdated }) {
  const { post } = useApi();
  const [confirmP, setConfirmP] = useState(null);
  const [removing, setRemoving] = useState(false);

  const participants = session.participants || [];
  const active = participants.filter(p => !p.removed);
  const removedCount = participants.length - active.length;

  const nameCounts = {};
  const deviceCounts = {};
  const ipCounts = {};
  active.forEach(p => {
    const n = (p.display_name || '').trim().toLowerCase();
    if (n) nameCounts[n] = (nameCounts[n] || 0) + 1;
    if (p.device_id) deviceCounts[p.device_id] = (deviceCounts[p.device_id] || 0) + 1;
    if (p.ip) ipCounts[p.ip] = (ipCounts[p.ip] || 0) + 1;
  });

  const flagsFor = (p) => {
    const flags = [];
    if (nameCounts[(p.display_name || '').trim().toLowerCase()] > 1)
      flags.push({ key: 'dup-name', label: 'Duplicate name', Icon: Copy });
    if (p.device_id && deviceCounts[p.device_id] > 1)
      flags.push({ key: 'same-device', label: 'Same device as another', Icon: MonitorSmartphone });
    else if (p.ip && ipCounts[p.ip] > 1)
      flags.push({ key: 'same-ip', label: 'Same network/IP as another', Icon: ShieldAlert });
    if (p.joined_at && session.started_at && p.joined_at > session.started_at)
      flags.push({ key: 'late-join', label: 'Joined after start', Icon: Clock3 });
    return flags;
  };

  const suspiciousCount = active.filter(p => flagsFor(p).length > 0).length;

  const remove = async () => {
    setRemoving(true);
    try {
      const updated = await post(
        `/live/sessions/${session.session_id}/participants/${confirmP.participant_id}/remove`, {}
      );
      toast.success(`${confirmP.display_name} removed — device blocked for this session.`);
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    } finally {
      setRemoving(false);
      setConfirmP(null);
    }
  };

  return (
    <Card data-testid="moderation-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="w-4 h-4" /> Participants ({active.length})
          {suspiciousCount > 0 && (
            <Badge variant="destructive" className="text-[10px]" data-testid="suspicious-count">
              {suspiciousCount} flagged
            </Badge>
          )}
          {removedCount > 0 && (
            <span className="text-[11px] text-muted-foreground font-normal ml-auto" data-testid="removed-count">
              {removedCount} removed
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 max-h-72 overflow-y-auto">
        {participants.length === 0 && (
          <p className="text-xs text-muted-foreground">Nobody has joined yet.</p>
        )}
        {participants.map(p => {
          const flags = p.removed ? [] : flagsFor(p);
          return (
            <div
              key={p.participant_id}
              className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
                p.removed ? 'opacity-50' : flags.length ? 'bg-red-500/5 border border-red-200/40' : 'hover:bg-muted/40'
              }`}
              data-testid={`mod-participant-${p.participant_id}`}
            >
              <span className={`truncate ${p.removed ? 'line-through' : ''}`}>
                {p.display_name}
                {p.email && <span className="text-[11px] text-muted-foreground ml-1.5">{p.email}</span>}
              </span>
              <span className="flex items-center gap-1 ml-auto">
                {flags.map(f => (
                  <span key={f.key} title={f.label} data-testid={`flag-${f.key}-${p.participant_id}`}>
                    <f.Icon className="w-3.5 h-3.5 text-red-500" />
                  </span>
                ))}
                {p.removed ? (
                  <Badge variant="secondary" className="text-[10px]">removed</Badge>
                ) : (
                  <Button
                    variant="ghost" size="sm"
                    className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                    title="Remove from session"
                    onClick={() => setConfirmP(p)}
                    data-testid={`remove-participant-${p.participant_id}`}
                  >
                    <UserX className="w-3.5 h-3.5" />
                  </Button>
                )}
              </span>
            </div>
          );
        })}
      </CardContent>

      <AlertDialog open={!!confirmP} onOpenChange={(o) => { if (!o) setConfirmP(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{confirmP?.display_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              They'll be struck from the leaderboard, can't answer anymore, and
              their device is blocked from re-joining this session. Their rows
              stay in the final report marked "removed by host".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); remove(); }}
              disabled={removing}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-remove-btn"
            >
              {removing && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
